import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { ConfigService } from '../../config/config.service';
import { debounceTime, map, startWith, switchMap, timeInterval } from 'rxjs/operators';
import { NavigationService } from '../../services/navigation.service';
import { PopoverService } from '../../components/popover/popover.service';
import { MegaMenuComponent } from '../../components/mega-menu/mega-menu.component';
import { Observable, of } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { SearchEngineService } from 'src/app/services/search.service';
import { MembersList } from 'src/app/models/MembersList';
import { Client } from 'src/app/models/Client';
import { MasterClientModel } from 'src/app/models/MasterClientModel';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Contact } from 'src/app/models/ContactModel';
import { Router } from '@angular/router';
import { NavigationItem } from 'src/@vex/interfaces/navigation-item.interface';
import { LawFirmDto } from 'src/app/models/LawFirm';
import { LawFirmContactSearch } from 'src/app/models/LawFirmContactSearch';

@Component({
  selector: 'vex-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent implements OnInit {

  @Input() mobileQuery: boolean;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  searchCtrl = new UntypedFormControl();
  clients: Client[];
  members: MembersList[];
  clientGroups: MasterClientModel[];
  contacts: Contact[];
  dependents: any[];
  lawFirms: LawFirmDto[];
  lawFirmContacts: LawFirmContactSearch[];
  isLoadingResults = false;
  query: string;

  @Input()
  @HostBinding('class.shadow-b')
  hasShadow: boolean;

  navigationItems: Observable<NavigationItem[]>;

  isHorizontalLayout$: Observable<boolean> = this.configService.config$.pipe(map(config => config.layout === 'horizontal'));
  isVerticalLayout$: Observable<boolean> = this.configService.config$.pipe(map(config => config.layout === 'vertical'));
  isNavbarInToolbar$: Observable<boolean> = this.configService.config$.pipe(map(config => config.navbar.position === 'in-toolbar'));
  isNavbarBelowToolbar$: Observable<boolean> = this.configService.config$.pipe(map(config => config.navbar.position === 'below-toolbar'));
  userVisible$: Observable<boolean> = this.configService.config$.pipe(map(config => config.toolbar.user.visible));

  megaMenuOpen$: Observable<boolean> = of(false);

  constructor(private layoutService: LayoutService,
              private configService: ConfigService,
              private navigationService: NavigationService,
              private popoverService: PopoverService,
              private searchService: SearchEngineService,
              private router: Router
              ) { }

  ngOnInit() {
    this.navigationItems = this.navigationService.items$;
    this.searchCtrl.valueChanges
    .pipe(
      debounceTime(1000)
    )
    .subscribe(query => {
      this.searchAll(query);
    });
  }

  openQuickpanel(): void {
    this.layoutService.openQuickpanel();
  }

  openSidenav(): void {
    this.layoutService.openSidenav();
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        content: MegaMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]
      })
    ).pipe(
      switchMap(popoverRef => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true),
    );
  }

  searchAll(query: string){

    this.query = query;
   
    this.members = [];
    this.clients = [];
    this.clientGroups = [];
    this.contacts = [];
    this.lawFirms = [];
    this.lawFirmContacts = [];

    const trimmed = (query ?? '').trim();
    if(!trimmed || trimmed.length < 3){
      return;
    }

    this.isLoadingResults = true;

    this.searchService.search(trimmed, 1, 10).subscribe((res) => {
      //fill the search results
      this.members= res.data?.members;
      this.clients = res.data?.clients;
      this.clientGroups = res.data?.clientGroups;
      this.contacts = res.data?.contacts;
      this.dependents = res.data?.dependents;
      this.lawFirms = res.data?.lawFirms;
      this.lawFirmContacts = res.data?.lawFirmContacts;
      this.isLoadingResults = false;
    });
  }

  displayFn(account: any): string {
    return '';
  }

  getMemberDetailId(item: { subId?: number; sub_id?: number }): number | undefined {
    return item?.subId ?? item?.sub_id;
  }


  handleOptionSelected(event: MatAutocompleteSelectedEvent) {
    const selectedValue = event.option.value;
    
    event.option.deselect();
    this.searchCtrl.setValue('');
    this.autocomplete.closePanel();

    // Navigate based on the option type (check for unique properties to identify type)
    if (selectedValue.masterCCODE && selectedValue.mName) {
      // Client Group
      this.router.navigate(['/clients', selectedValue.masterCCODE]);
    } else if (selectedValue.masterCcode && selectedValue.ccodes && selectedValue.cname) {
      // Sub Client
      this.router.navigate(['/clients', selectedValue.masterCcode, 'subclients', selectedValue.ccodes]);
    } else if (selectedValue.dependentId != null || selectedValue.dependent_id != null) {
      // Dependent
      const subId = this.getMemberDetailId(selectedValue);
      if (subId != null) {
        this.router.navigate(['/members/detail', subId]);
      }
    } else if (selectedValue.sub_id && selectedValue.last_name && selectedValue.first_name) {
      // Member
      this.router.navigate(['/members/detail', selectedValue.sub_id]);
    } else if (selectedValue.fullName && selectedValue.mCode) {
      // Contact
      if (selectedValue.mCode && !selectedValue.cCode) {
        this.router.navigate(['/clients', selectedValue.mCode, 'contacts']);
      } else if (selectedValue.mCode && selectedValue.cCode) {
        this.router.navigate(['/clients', selectedValue.mCode, 'subclients', selectedValue.cCode, 'contacts']);
      }
    } else if (selectedValue.contactId != null && selectedValue.firmCode) {
      // Law Firm Contact
      this.router.navigate(['/law-firm', selectedValue.firmCode], { queryParams: { contact: selectedValue.contactId } });
    } else if (selectedValue.firmCode && selectedValue.firmName) {
      // Law Firm
      const route = selectedValue.isGroup === true ? 'law-firm-group' : 'law-firm';
      this.router.navigate(['/', route, selectedValue.firmCode]);
    }
  }

  onKeyEnter() {
    const trimmed = (this.query ?? '').trim();
    if(!trimmed || trimmed.length < 3){
      return;
    }
    this.router.navigate(['search', trimmed]);
  }
}
